import { stripDocumentIdFromName } from '../shared/fileNameUtils';

/**
 * File Upload Service
 * Centralized utilities for handling file uploads and metadata
 */

/**
 * Extract file size from various response formats
 * @param {Object} fileOrResponse - File object or API response
 * @returns {number} File size in bytes
 */
export const extractFileSize = (fileOrResponse) => {
    if (!fileOrResponse) return 0;
    
    // Direct size property
    if (typeof fileOrResponse.size === 'number' && fileOrResponse.size > 0) {
        return fileOrResponse.size;
    }
    
    // Common nested patterns from EDM responses
    const paths = ['data.size', 'file.size', 'edmResponse.size', 'fileSize'];
    for (const path of paths) {
        const value = path.split('.').reduce((obj, key) => obj?.[key], fileOrResponse);
        if (typeof value === 'number' && value > 0) return value;
    }
    
    // Deep scan for size property (last resort)
    if (typeof fileOrResponse === 'object') {
        for (const key of Object.keys(fileOrResponse)) {
            const val = fileOrResponse[key];
            if (val && typeof val === 'object' && typeof val.size === 'number' && val.size > 0) {
                return val.size;
            }
        }
    }
    
    return 0;
};

/**
 * Normalize file metadata from various sources
 * @param {Object} file - Original file data
 * @param {Object} edmResponse - EDM upload response
 * @returns {Object} Normalized file metadata
 */
export const normalizeFileMetadata = (file = {}, edmResponse = null) => {
    const incomingDocumentId = file.documentId || file.id || edmResponse?.documentId || edmResponse?.data?.documentId;
    const edmFileId = edmResponse?.edmFileId || edmResponse?.id || edmResponse?.fileId || edmResponse?.data?.id || file.edmFileId;
    const resolvedId = incomingDocumentId || edmFileId || file.id;
    const size = extractFileSize(file) || extractFileSize(edmResponse);
    let status = file.status || (edmFileId ? 'success' : undefined) || 'uploading';
    if (file.errorMessage) {
        status = 'error';
    }

    const rawName = file.name || edmResponse?.name || edmResponse?.fileName || resolvedId;
    const displayName = stripDocumentIdFromName(rawName, resolvedId) || rawName;

    return {
        id: resolvedId,
        documentId: resolvedId,
        edmFileId,
        name: displayName,
        size,
        mimeType: file.type || file.mimeType || edmResponse?.mimeType,
        uploadedAt: file.uploadedAt || new Date().toISOString(),
        status,
        errorMessage: file.errorMessage
    };
};

/**
 * Normalize sizes in supplemental items (for backward compatibility)
 * @param {Object|Array} supplementalItems - Supplemental items to normalize
 */
export const normalizeSupplementalSizes = (supplementalItems) => {
    if (!supplementalItems) return;
    
    const normalizeEntry = (entry) => {
        if (!entry) return;
        
        // Normalize files array
        if (entry.files && Array.isArray(entry.files)) {
            entry.files.forEach(file => {
                if (!file.size || Number(file.size) === 0) {
                    file.size = extractFileSize(file);
                }
            });
        }
        
        // Normalize entry size
        if (!entry.size || Number(entry.size) === 0) {
            entry.size = extractFileSize(entry);
        }
    };
    
    if (Array.isArray(supplementalItems)) {
        supplementalItems.forEach(normalizeEntry);
    } else if (typeof supplementalItems === 'object') {
        Object.values(supplementalItems).forEach(normalizeEntry);
    }
};

/**
 * Find file metadata from supplemental items store
 * @param {string} fileId - File ID (documentId) to find
 * @param {Object} supplementalItems - Supplemental items object (question-keyed structure)
 * @param {Object} supplementalItemsMap - Supplemental items map (optional, for fast lookup)
 * @returns {Object|undefined} File metadata
 */
export const findFileMetadata = (fileId, supplementalItems = {}, supplementalItemsMap = {}) => {
    // Check map first (faster)
    if (supplementalItemsMap[fileId]) {
        return supplementalItemsMap[fileId];
    }
    
    // Check direct key (fallback mode when questionId is undefined)
    if (supplementalItems[fileId] && typeof supplementalItems[fileId] === 'object') {
        // Check if this is a direct file metadata (has documentId or name, but not files array)
        const entry = supplementalItems[fileId];
        if (entry.documentId || entry.name || entry.edmFileId) {
            if (!Array.isArray(entry.files)) {
                return entry;
            }
        }
    }
    
    // Search through question-keyed structure with files arrays
    for (const key of Object.keys(supplementalItems)) {
        const entry = supplementalItems[key];
        if (!entry) continue;
        
        // Check files array (canonical structure: supplementalItems[questionId].files = [...])
        if (Array.isArray(entry.files)) {
            const match = entry.files.find(f => 
                f?.documentId === fileId || f?.id === fileId
            );
            if (match) return match;
        }
    }
    
    return undefined;
};